import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import styles from '../assets/propertiesMonthlyInspections.module.css';
import { propertiesMonthlyInspectionsService } from '../services/propertiesMonthlyInspectionsService';
import inspectionsService from '../services/inspectionsService';
import { useAuth } from '../contexts/AuthContext';

const SortIcon = ({ direction, isActive }) => (
  <svg
    width="10"
    height="16"
    viewBox="0 0 10 16"
    style={{ marginLeft: 4, verticalAlign: "middle" }}
  >
    <g fill={isActive ? "#333" : "#bbb"}>
      {direction === "asc" ? (
        <polygon points="5,3 8,7 2,7" />
      ) : direction === "desc" ? (
        <polygon points="5,13 2,9 8,9" />
      ) : (
        <>
          <polygon points="5,3 8,7 2,7" />
          <polygon points="5,13 2,9 8,9" />
        </>
      )}
    </g>
  </svg>
);

const PropertiesMonthlyInspections = () => {
  const navigate = useNavigate();
  const { isGuardianManager } = useAuth();
  const [loading, setLoading] = useState(false); // Start as false since we don't load data on mount
  const [properties, setProperties] = useState([]);
  const [useMockData, setUseMockData] = useState(false); // Always use live data for Monthly Inspection Reports
  const [apiStatus, setApiStatus] = useState('connecting');
  const [entriesPerPage, setEntriesPerPage] = useState(30);
  const [tableSearch, setTableSearch] = useState('');
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [columnVisibility, setColumnVisibility] = useState({
    property: true,
    id: true,
    postcode: true,
    company: true,
    dateLive: true,
    am: true,
    bm: true,
    route: true,
    status: true,
    actions: true
  });
  const [showColumnVisibility, setShowColumnVisibility] = useState(false);
  
  // State for table sorting
  const [sortField, setSortField] = useState("property");
  const [sortDirection, setSortDirection] = useState("asc");

  const [isStatusDescriptionsCollapsed, setIsStatusDescriptionsCollapsed] = useState(false);
  
  // Search form state (current values in dropdowns/inputs)
  const [searchForm, setSearchForm] = useState({
    accountManagerId: 'all',
    inspectionRouteId: 'all',
    clientId: '',
    clientName: '',
    propertyId: '',
    propertyName: ''
  });

  // Applied filters state (only updated when Search button is clicked)
  const [appliedFilters, setAppliedFilters] = useState({
    accountManagerId: 'all',
    inspectionRouteId: 'all',
    clientId: '',
    clientName: '',
    propertyId: '',
    propertyName: ''
  });

  // Property status filter state for monthly inspections
  const [selectedPropertyStatusFilter, setSelectedPropertyStatusFilter] = useState(null); // null = all, 'upcoming', 'scheduled', 'completed', 'to_be_published', 'past_completed', 'overdue'

  // Typeahead state for monthly inspections
  const [clientTypeaheadResults, setClientTypeaheadResults] = useState([]);
  const [showClientTypeahead, setShowClientTypeahead] = useState(false);
  const [propertyTypeaheadResults, setPropertyTypeaheadResults] = useState([]);
  const [showPropertyTypeahead, setShowPropertyTypeahead] = useState(false);

  // Account managers and routes from backend
  const [accountManagers, setAccountManagers] = useState([]);
  const [routes, setRoutes] = useState([]);

  // Track if initial search has been performed (table should not load on first load)
  const [hasSearched, setHasSearched] = useState(false);



  // Status overview state
  const [statusOverview, setStatusOverview] = useState({
    propertiesUpcoming: 0,
    propertiesInspectionScheduled: 0,
    propertiesInspectionCompleted: 0,
    propertiesToBePublished: 0,
    propertiesCompleted: 0,
    propertiesOverdue: 0
  });

  // Mock data generation
  const generateMockProperties = () => {
    return [
      {
        id: 1,
        propertyName: 'Sunset Apartments',
        propertyPublicId: 'PROP001',
        propertyPostcode: 'SW1A 1AA',
        clientName: 'Sunset Properties Ltd',
        dateLive: '2024-01-15',
        accountManager: 'John Smith',
        buildingManager: 'Mike Johnson',
        routeName: 'Central London',
        monthlyInspectionStatus: 'To be Scheduled',
        statusColor: '#5bc0de'
      },
      {
        id: 2,
        propertyName: 'Riverside House',
        propertyPublicId: 'PROP002',
        propertyPostcode: 'E1 1AA',
        clientName: 'Riverside Holdings',
        dateLive: '2024-02-01',
        accountManager: 'Sarah Wilson',
        buildingManager: 'David Brown',
        routeName: 'East London',
        monthlyInspectionStatus: 'Inspection Scheduled',
        statusColor: '#f0ad4e'
      },
      {
        id: 3,
        propertyName: 'Park View Gardens',
        propertyPublicId: 'PROP003',
        propertyPostcode: 'N1 1AA',
        clientName: 'Park View Ltd',
        dateLive: '2024-01-20',
        accountManager: 'Emma Davis',
        buildingManager: 'Tom Wilson',
        routeName: 'North London',
        monthlyInspectionStatus: 'Completed',
        statusColor: '#5cb85c'
      },
      {
        id: 4,
        propertyName: 'Harbour Lights',
        propertyPublicId: 'PROP004',
        propertyPostcode: 'SE1 1AA',
        clientName: 'Harbour Properties',
        dateLive: '2024-01-10',
        accountManager: 'James Miller',
        buildingManager: 'Lisa Anderson',
        routeName: 'South London',
        monthlyInspectionStatus: 'To be published',
        statusColor: '#337ab7'
      },
      {
        id: 5,
        propertyName: 'Mountain View',
        propertyPublicId: 'PROP005',
        propertyPostcode: 'W1 1AA',
        clientName: 'Mountain Holdings',
        dateLive: '2024-01-05',
        accountManager: 'Robert Taylor',
        buildingManager: 'Anna Garcia',
        routeName: 'West London',
        monthlyInspectionStatus: 'Past Inspection To Complete',
        statusColor: '#5cb85c'
      },
      {
        id: 6,
        propertyName: 'Ocean Heights',
        propertyPublicId: 'PROP006',
        propertyPostcode: 'NW1 1AA',
        clientName: 'Ocean Properties',
        dateLive: '2024-01-12',
        accountManager: 'Michael Clark',
        buildingManager: 'Rachel Lee',
        routeName: 'North West London',
        monthlyInspectionStatus: 'Overdue',
        statusColor: '#d9534f'
      }
    ];
  };

  const generateMockAccountManagers = () => {
    return [
      { id: 1, name: 'John Smith' },
      { id: 2, name: 'Sarah Wilson' },
      { id: 3, name: 'Emma Davis' },
      { id: 4, name: 'James Miller' },
      { id: 5, name: 'Robert Taylor' },
      { id: 6, name: 'Michael Clark' }
    ];
  };

  const generateMockRoutes = () => {
    return [
      { id: 1, name: 'Central London' },
      { id: 2, name: 'East London' },
      { id: 3, name: 'North London' },
      { id: 4, name: 'South London' },
      { id: 5, name: 'West London' },
      { id: 6, name: 'North West London' }
    ];
  };

  useEffect(() => {
    // Load dropdown data, status overview, and "To be Scheduled" properties on mount
    console.log('🚀 [PropertiesMonthlyInspections] Component mounted, loading initial data...');
    loadAccountManagersAndRoutes();
    loadStatusOverview();

    // Auto-load "To be Scheduled" properties on page load
    setSelectedPropertyStatusFilter('upcoming');
    setHasSearched(true);
    loadProperties('upcoming');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Load dropdowns, overview, and "To be Scheduled" data on mount

  // Load account managers and routes for dropdowns
  // Use same endpoint as Inspections.jsx to ensure consistency
  const loadAccountManagersAndRoutes = async () => {
    try {
      // Use inspections index endpoint to get dropdown data (same as Inspections.jsx)
      const response = await inspectionsService.getInspectionsIndex();
      
      if (response.success && response.data) {
        // Extract account managers and routes from response (same structure as Inspections.jsx)
        const accountManagersData = response.data.accountManagers || [];
        const routesData = response.data.routes || [];
        
        // Transform routes to match expected format
        // Routes can come as array of objects or as object with id:name pairs
        let transformedRoutes = [];
        if (Array.isArray(routesData)) {
          transformedRoutes = routesData.map(route => {
            if (typeof route === 'object') {
              // If it's already {id, name} format
              if (route.id && route.name) {
                return route;
              }
              // If it's {id: name} format (Laravel pluck format)
              const keys = Object.keys(route);
              if (keys.length > 0) {
                return { id: keys[0], name: route[keys[0]] };
              }
            }
            return route;
          });
        } else if (typeof routesData === 'object') {
          // If routes is an object with id:name pairs (Laravel pluck format)
          transformedRoutes = Object.keys(routesData).map(id => ({
            id: parseInt(id) || id,
            name: routesData[id]
          }));
        }
        
        setAccountManagers(accountManagersData);
        setRoutes(transformedRoutes);
        
        console.log('✅ Loaded dropdowns from inspections index:', {
          accountManagers: accountManagersData.length,
          routes: transformedRoutes.length
        });
      } else {
        // Fallback: try separate endpoints
        try {
          const amResponse = await propertiesMonthlyInspectionsService.getAccountManagers();
          if (amResponse && amResponse.data && Array.isArray(amResponse.data)) {
            setAccountManagers(amResponse.data);
          }
        } catch (e) {
          console.error('Error loading account managers:', e);
        }
        
        try {
          const routesResponse = await propertiesMonthlyInspectionsService.getInspectionRoutes();
          if (routesResponse && routesResponse.data && Array.isArray(routesResponse.data)) {
            setRoutes(routesResponse.data);
          }
        } catch (e) {
          console.error('Error loading routes:', e);
        }
      }
    } catch (error) {
      console.error('Error loading account managers and routes:', error);
      // Fallback to empty arrays
      setAccountManagers([]);
      setRoutes([]);
    } finally {
      // Ensure loading is false after dropdowns load
      setLoading(false);
    }
  };

  useEffect(() => {
    // Filter properties based on applied filters (only updated when Search button is clicked)
    let filtered = properties;

    // Apply form filters using appliedFilters (not searchForm)
    if (appliedFilters.accountManagerId && appliedFilters.accountManagerId !== 'all') {
      filtered = filtered.filter(property => {
        // Filter by account manager ID if available, otherwise match by name
        if (property.accountManagerId) {
          return property.accountManagerId.toString() === appliedFilters.accountManagerId.toString();
        }
        // Fallback: match by account manager name
        const selectedManager = accountManagers.find(m => m.id.toString() === appliedFilters.accountManagerId.toString());
        if (selectedManager) {
          return property.accountManager === selectedManager.name;
        }
        return false;
      });
    }

    if (appliedFilters.inspectionRouteId && appliedFilters.inspectionRouteId !== 'all') {
      filtered = filtered.filter(property => {
        // Filter by route ID if available, otherwise match by name
        if (property.routeId) {
          return property.routeId.toString() === appliedFilters.inspectionRouteId.toString();
        }
        // Fallback: match by route name
        const selectedRoute = routes.find(r => r.id.toString() === appliedFilters.inspectionRouteId.toString());
        if (selectedRoute) {
          return property.routeName === selectedRoute.name;
        }
        return false;
      });
    }

    // Filter by client ID if selected, otherwise filter by name
    if (appliedFilters.clientId) {
      filtered = filtered.filter(property =>
        property.clientId?.toString() === appliedFilters.clientId.toString()
      );
    } else if (appliedFilters.clientName) {
      const clientName = appliedFilters.clientName.toLowerCase();
      filtered = filtered.filter(property =>
        property.clientName?.toLowerCase().includes(clientName)
      );
    }

    // Filter by property ID if selected (exact match only), otherwise filter by name
    if (appliedFilters.propertyId) {
      filtered = filtered.filter(property =>
        property.id?.toString() === appliedFilters.propertyId.toString()
      );
    } else if (appliedFilters.propertyName) {
      const propertyName = appliedFilters.propertyName.toLowerCase();
      filtered = filtered.filter(property =>
        property.propertyName?.toLowerCase().includes(propertyName) ||
        property.propertyPublicId?.toLowerCase().includes(propertyName)
      );
    }
    
    // Apply property status filter from overview boxes
    if (selectedPropertyStatusFilter) {
      const statusMap = {
        'upcoming': ['To be Scheduled', 'Upcoming', 'To Be Scheduled'],
        'scheduled': ['Inspection Scheduled', 'Scheduled'],
        'completed': ['Completed'],
        'to_be_published': ['To be published', 'To Be Published'],
        'past_completed': ['Past Inspection To Complete', 'Past Inspection To Complete'],
        'overdue': ['Overdue']
      };
      
      const allowedStatuses = statusMap[selectedPropertyStatusFilter] || [];
      if (allowedStatuses.length > 0) {
        filtered = filtered.filter(property => {
          const propertyStatus = property.monthlyInspectionStatus || '';
          return allowedStatuses.some(status => 
            propertyStatus.toLowerCase() === status.toLowerCase()
          );
        });
      }
    }
    
    // Apply table search
    if (tableSearch) {
      const searchTerm = tableSearch.toLowerCase();
      filtered = filtered.filter(property => 
        property.propertyName?.toLowerCase().includes(searchTerm) ||
        property.propertyPublicId?.toLowerCase().includes(searchTerm) ||
        property.propertyPostcode?.toLowerCase().includes(searchTerm) ||
        property.clientName?.toLowerCase().includes(searchTerm) ||
        property.accountManager?.toLowerCase().includes(searchTerm) ||
        property.buildingManager?.toLowerCase().includes(searchTerm) ||
        property.routeName?.toLowerCase().includes(searchTerm) ||
        property.monthlyInspectionStatus?.toLowerCase().includes(searchTerm)
      );
    }
    
    // Apply sorting
    filtered = [...filtered].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle nested object sorting - map sortField to actual data field names
      if (sortField === "property") {
        aValue = a.propertyName || "";
        bValue = b.propertyName || "";
      } else if (sortField === "id") {
        aValue = a.propertyPublicId || "";
        bValue = b.propertyPublicId || "";
      } else if (sortField === "postcode") {
        aValue = a.propertyPostcode || "";
        bValue = b.propertyPostcode || "";
      } else if (sortField === "company") {
        aValue = a.clientName || "";
        bValue = b.clientName || "";
      } else if (sortField === "dateLive") {
        aValue = new Date(aValue || 0);
        bValue = new Date(bValue || 0);
      } else if (sortField === "am") {
        aValue = a.accountManager || "";
        bValue = b.accountManager || "";
      } else if (sortField === "bm") {
        aValue = a.buildingManager || "";
        bValue = b.buildingManager || "";
      } else if (sortField === "route") {
        aValue = a.routeName || "";
        bValue = b.routeName || "";
      } else if (sortField === "status") {
        aValue = a.monthlyInspectionStatus || "";
        bValue = b.monthlyInspectionStatus || "";
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
    
    // Calculate pagination
    const total = filtered.length;
    const pages = Math.ceil(total / entriesPerPage);
    setTotalPages(pages);
    setTotalRecords(total);
    
    // Ensure current page is valid
    const validPage = Math.min(currentPage, pages || 1);
    if (validPage !== currentPage) {
      setCurrentPage(validPage);
    }
    
    // Get current page data
    const startIndex = (validPage - 1) * entriesPerPage;
    const endIndex = startIndex + entriesPerPage;
    setFilteredProperties(filtered.slice(startIndex, endIndex));
  }, [properties, tableSearch, appliedFilters, entriesPerPage, useMockData, currentPage, sortField, sortDirection, selectedPropertyStatusFilter, accountManagers, routes]);

  // Load properties - now accepts optional status filter parameter
  const loadProperties = async (statusFilter = null) => {
    setLoading(true);
    setApiStatus('connecting');
    
    try {
      if (useMockData) {
        console.log("🔄 Using mock data");
        setApiStatus('mock');
        // Simulate API call delay
        await new Promise(resolve => setTimeout(resolve, 500));
        const mockData = generateMockProperties();
        setProperties(mockData);
        setFilteredProperties(mockData.slice(0, entriesPerPage));
        setTotalRecords(mockData.length);
        
        // DON'T update status overview - it should remain static from initial load
        // Status overview shows overall counts, not filtered counts
      } else {
        console.log("🔄 Calling real API...", { statusFilter });
        setApiStatus('loading');
        
        // Build request parameters
        const requestParams = {
          draw: 1,
          start: 0,
          length: 10000 // Get all properties
        };
        
        // Map frontend filter IDs to backend status values if needed
        if (statusFilter) {
          const statusMap = {
            'upcoming': 'To be Scheduled',
            'scheduled': 'Inspection Scheduled',
            'completed': 'Completed',
            'to_be_published': 'To be published',
            'past_completed': 'Past Inspection To Complete',
            'overdue': 'Overdue'
          };
          requestParams.status = statusMap[statusFilter] || statusFilter;
        }
        
        // Include account manager filter if selected
        if (searchForm.accountManagerId && searchForm.accountManagerId !== 'all') {
          requestParams.account_manager_id = searchForm.accountManagerId;
        }
        
        // Include route filter if selected
        if (searchForm.inspectionRouteId && searchForm.inspectionRouteId !== 'all') {
          requestParams.route_id = searchForm.inspectionRouteId;
        }
        
        const response = await propertiesMonthlyInspectionsService.getProperties(requestParams);
        console.log("📡 API Response:", response);
        
        setApiStatus('connected');
        
        // Handle DataTables response format
        if (response.data && response.data.data && response.data.data.data && Array.isArray(response.data.data.data)) {
          // DataTables format with nested structure - use data directly since backend provides both formats
          const properties = response.data.data.data;
          setProperties(properties);
          setFilteredProperties(properties.slice(0, entriesPerPage));
          setTotalRecords(response.data.data.recordsTotal || properties.length);
          // DON'T update status overview - it should remain static from initial load
          // Status overview shows overall counts, not filtered counts
          console.log("✅ Properties loaded from API:", properties.length, properties[0]);
        } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
          // DataTables format - use data directly since backend provides both formats
          const properties = response.data.data;
          setProperties(properties);
          setFilteredProperties(properties.slice(0, entriesPerPage));
          setTotalRecords(response.data.recordsTotal || properties.length);
          // DON'T update status overview - it should remain static from initial load
          // Status overview shows overall counts, not filtered counts
          console.log("✅ Properties loaded from API:", properties.length, properties[0]);
        } else if (Array.isArray(response.data)) {
          // Direct array format
          setProperties(response.data);
          setFilteredProperties(response.data.slice(0, entriesPerPage));
          setTotalRecords(response.data.length);
          // DON'T update status overview - it should remain static from initial load
          // Status overview shows overall counts, not filtered counts
          console.log("✅ Properties loaded (array format):", response.data.length);
        } else {
          console.warn("⚠️ Unexpected response format:", response);
          console.warn("⚠️ Response.data:", response.data);
          console.warn("⚠️ Response.data.data:", response.data?.data);
          console.warn("⚠️ Is array:", Array.isArray(response.data));
          // Fallback
          setProperties([]);
          setFilteredProperties([]);
          setTotalRecords(0);
        }
      }
    } catch (error) {
      console.error('❌ Error loading properties:', error);
      setApiStatus('error');
      // toast.error(`Failed to load properties: ${error.message}`); // Hidden as requested
      
      // Auto-fallback to mock data on error
      console.log("🔄 Auto-falling back to mock data...");
      const mockData = generateMockProperties();
      setProperties(mockData);
      setFilteredProperties(mockData.slice(0, entriesPerPage));
      setTotalRecords(mockData.length);
      // DON'T update status overview - it should remain static from initial load
      // Status overview shows overall counts, not filtered counts
      setApiStatus('mock-fallback');
    } finally {
      setLoading(false);
    }
  };

  const updateStatusOverview = (properties) => {
    const overview = {
      propertiesUpcoming: properties.filter(p => p.monthlyInspectionStatus === 'To be Scheduled').length,
      propertiesInspectionScheduled: properties.filter(p => p.monthlyInspectionStatus === 'Inspection Scheduled').length,
      propertiesInspectionCompleted: properties.filter(p => p.monthlyInspectionStatus === 'Completed').length,
      propertiesToBePublished: properties.filter(p => p.monthlyInspectionStatus === 'To be published').length,
      propertiesCompleted: properties.filter(p => p.monthlyInspectionStatus === 'Past Inspection To Complete').length,
      propertiesOverdue: properties.filter(p => p.monthlyInspectionStatus === 'Overdue').length
    };
    setStatusOverview(overview);
  };

  // Load status overview statistics on page load
  // Use EXACT same endpoint and logic as Inspections.jsx to ensure identical counts
  const loadStatusOverview = async () => {
    try {
      console.log('📡 [PropertiesMonthlyInspections] Loading status overview statistics from inspections index (same as Inspections.jsx)...');
      
      // Use EXACT same endpoint as Inspections.jsx to get propertiesOverview
      // This ensures both pages show the same counts
      const response = await inspectionsService.getInspectionsIndex();
      console.log('📡 [PropertiesMonthlyInspections] Inspections index response:', response);
      
      // inspectionsService.getInspectionsIndex() returns response.data directly
      // Response structure: { success: true, data: { propertiesOverview: {...} } }
      if (response && response.success) {
        const { data } = response;
        
        // Extract properties overview from response (EXACT same structure as Inspections.jsx line 171)
        const propertiesOverview = data.propertiesOverview || {};
        console.log('📡 [PropertiesMonthlyInspections] Extracted propertiesOverview:', propertiesOverview);
        
        // Set status overview (EXACT same logic as Inspections.jsx lines 172-177)
        setStatusOverview({
          propertiesUpcoming: propertiesOverview.propertiesUpcoming || 0,
          propertiesInspectionScheduled: propertiesOverview.propertiesInspectionScheduled || 0,
          propertiesInspectionCompleted: propertiesOverview.propertiesInspectionCompleted || 0,
          propertiesToBePublished: propertiesOverview.propertiesToBePublished || 0,
          propertiesCompleted: propertiesOverview.propertiesCompleted || 0,
          propertiesOverdue: propertiesOverview.propertiesOverdue || 0
        });
        
        console.log('✅ [PropertiesMonthlyInspections] Status overview loaded from inspections index (same as Inspections.jsx):', {
          propertiesUpcoming: propertiesOverview.propertiesUpcoming || 0,
          propertiesInspectionScheduled: propertiesOverview.propertiesInspectionScheduled || 0,
          propertiesInspectionCompleted: propertiesOverview.propertiesInspectionCompleted || 0,
          propertiesToBePublished: propertiesOverview.propertiesToBePublished || 0,
          propertiesCompleted: propertiesOverview.propertiesCompleted || 0,
          propertiesOverdue: propertiesOverview.propertiesOverdue || 0
        });
      } else {
        console.error('❌ [PropertiesMonthlyInspections] Failed to load status overview:', response);
        // Keep default values (all zeros) - already set in initial state
      }
    } catch (error) {
      console.error('❌ [PropertiesMonthlyInspections] Error loading status overview:', error);
      console.error('❌ [PropertiesMonthlyInspections] Error details:', error.message, error.stack);
      // Keep default values (all zeros) - already set in initial state
    }
  };



  const handleClearFilters = () => {
    const defaultFilters = {
      accountManagerId: 'all',
      inspectionRouteId: 'all',
      clientId: '',
      clientName: '',
      propertyId: '',
      propertyName: ''
    };
    setSearchForm(defaultFilters);
    setAppliedFilters(defaultFilters); // Also clear applied filters
    setTableSearch('');
    setCurrentPage(1);
    setSelectedPropertyStatusFilter(null);
    setHasSearched(false); // Reset search state
    // Clear table data
    setProperties([]);
    setFilteredProperties([]);
    setTotalRecords(0);
    // DON'T update status overview - it should remain static from initial load
    // Status overview shows overall counts, not filtered counts
  };

  const handleSearchChange = (field, value) => {
    setSearchForm(prev => ({
      ...prev,
      [field]: value
    }));
    // Only update form state - filters apply when Search button is clicked
  };

  const handleStatusBoxClick = (statusId) => {
    console.log('Status box clicked:', statusId);
    
    // Mark that search has been performed
    setHasSearched(true);
    
    // Always filter by the selected status (no toggle - always apply filter)
    setSelectedPropertyStatusFilter(statusId);
    
    // Map statusId to display name for toast
    const statusNames = {
      'upcoming': 'To be Scheduled',
      'scheduled': 'Inspection Scheduled',
      'completed': 'Completed',
      'to_be_published': 'To be published',
      'past_completed': 'Past Inspection To Complete',
      'overdue': 'Overdue'
    };
    
    toast.info(`Filtering by status: ${statusNames[statusId] || statusId}...`);
    
    // Load data with the selected filter (and any account manager/route filters)
    loadProperties(statusId);
    
    // Reset to first page when filter changes
    setCurrentPage(1);
  };

  // Handle client search for typeahead
  const handleClientSearch = async (value) => {
    setSearchForm(prev => ({
      ...prev,
      clientName: value,
      clientId: '' // Clear ID when name changes
    }));

    if (value.length >= 1) {
      try {
        const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const response = await fetch(`${backendUrl}/api/clients/ajax/${encodeURIComponent(value)}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        const data = await response.json();
        if (data.success && data.data) {
          setClientTypeaheadResults(data.data);
          setShowClientTypeahead(true);
        } else {
          setClientTypeaheadResults([]);
          setShowClientTypeahead(false);
        }
      } catch (error) {
        console.error('Error searching clients:', error);
        setClientTypeaheadResults([]);
        setShowClientTypeahead(false);
      }
    } else {
      setClientTypeaheadResults([]);
      setShowClientTypeahead(false);
    }
  };

  // Handle client selection
  const handleClientSelect = (client) => {
    setSearchForm(prev => ({
      ...prev,
      clientId: client.id,
      clientName: client.name
    }));
    setShowClientTypeahead(false);
  };

  // Handle property search for typeahead
  const handlePropertySearch = async (value) => {
    setSearchForm(prev => ({
      ...prev,
      propertyName: value,
      propertyId: '' // Clear ID when name changes
    }));

    if (value.length >= 1) {
      try {
        // Use same endpoint as Inspections.jsx for consistency
        const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const response = await fetch(`${backendUrl}/api/properties/all/search/${encodeURIComponent(value)}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        const data = await response.json();
        if (data.success && data.data) {
          setPropertyTypeaheadResults(data.data);
          setShowPropertyTypeahead(true);
        } else {
          setPropertyTypeaheadResults([]);
          setShowPropertyTypeahead(false);
        }
      } catch (error) {
        console.error('Error searching properties:', error);
        setPropertyTypeaheadResults([]);
        setShowPropertyTypeahead(false);
      }
    } else {
      setPropertyTypeaheadResults([]);
      setShowPropertyTypeahead(false);
    }
  };

  // Handle property selection
  const handlePropertySelect = (property) => {
    setSearchForm(prev => ({
      ...prev,
      propertyId: property.id,
      propertyName: property.name
    }));
    setShowPropertyTypeahead(false);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page on search
    setHasSearched(true); // Mark that search has been performed

    // Apply current form values to filters (filters only apply when Search is clicked)
    setAppliedFilters({ ...searchForm });

    // Load data with current filters
    // Note: Toast removed since table shows "(Showing X of Y results)" which is more accurate
    // because client-side filtering (property/client name) happens asynchronously in useEffect
    const statusFilter = selectedPropertyStatusFilter;
    loadProperties(statusFilter);
  };

  const handlePropertyAction = (action, propertyId) => {
    const property = properties.find(p => p.id === propertyId);
    
    switch (action) {
      case 'show':
        navigate(`/properties/${propertyId}`);
        break;
      case 'edit':
        navigate(`/properties/${propertyId}/edit`);
        break;
      case 'remove':
        if (window.confirm(`Are you sure you want to remove ${property?.propertyName}? This will delete the property and all related records from the database.`)) {
          handleRemoveProperty(propertyId);
        }
        break;
      case 'payments':
        navigate(`/properties/${propertyId}/payments`);
        break;
      case 'photos':
        navigate(`/properties/${propertyId}/photos`);
        break;
      case 'viewings':
        navigate(`/properties/${propertyId}/viewings`);
        break;
      case 'monthly-inspections':
        navigate(`/properties/${propertyId}/monthly-inspections`);
        break;
      case 'copy-address':
        if (property) {
          const address = `${property.propertyName}, ${property.propertyPostcode}`;
          navigator.clipboard.writeText(address).then(() => {
            toast.success('Address copied to clipboard!');
          }).catch(() => {
            toast.error('Failed to copy address');
          });
        }
        break;
      default:
        console.log('Unknown action:', action);
    }
  };

  const handleRemoveProperty = async (propertyId) => {
    try {
      if (useMockData) {
        // Remove from mock data
        const updatedProperties = properties.filter(property => property.id !== propertyId);
        setProperties(updatedProperties);
        toast.success('Property removed successfully (Mock mode)');
      } else {
        // Call API to delete property and all related records
        const response = await propertiesMonthlyInspectionsService.deleteProperty(propertyId);
        
        if (response.success) {
          // Remove from local state
          const updatedProperties = properties.filter(property => property.id !== propertyId);
          setProperties(updatedProperties);
          
          // Show success message with details about deleted records
          const deletedRecords = response.data?.deleted_records || [];
          const recordCount = deletedRecords.length;
          toast.success(`Property removed successfully! Deleted ${recordCount} related record types: ${deletedRecords.join(', ')}`);
        } else {
          toast.error(response.message || 'Failed to remove property');
        }
      }
    } catch (error) {
      console.error('Error removing property:', error);
      toast.error('Failed to remove property');
    }
  };

  const toggleMockData = () => {
    setUseMockData(!useMockData);
  };

  const toggleStatusDescriptions = () => {
    setIsStatusDescriptionsCollapsed(!isStatusDescriptionsCollapsed);
  };

  // Pagination functions
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const goToPreviousPage = () => {
    goToPage(currentPage - 1);
  };

  const goToNextPage = () => {
    goToPage(currentPage + 1);
  };

  const getPageNumbers = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }
    return pages;
  };

  const handleExportCSV = () => {
    try {
      const csvContent = [
        ['Property', 'ID', 'Postcode', 'Company', 'Date Live', 'AM', 'BM', 'Route', 'Status'],
        ...filteredProperties.map(property => [
          property.propertyName,
          property.propertyPublicId,
          property.propertyPostcode,
          property.clientName,
          property.dateLive,
          property.accountManager,
          property.buildingManager,
          property.routeName,
          property.monthlyInspectionStatus
        ])
      ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `properties-monthly-inspections-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported successfully!');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Failed to export CSV');
    }
  };

  const handleExportPDF = async () => {
    try {
      // Dynamic import for jsPDF
      let jsPDF;
      try {
        jsPDF = (await import('jspdf')).default;
      } catch (importError) {
        console.error('Failed to import jsPDF dynamically:', importError);
        // Fallback to require
        jsPDF = require('jspdf').default;
      }

      // Create new PDF document
      const doc = new jsPDF();
      
      // Add title
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Properties Monthly Inspections', 20, 20);
      
      // Add date
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 20, 30);
      
      // Add total records
      doc.text(`Total Records: ${filteredProperties.length}`, 20, 40);
      
      // Set up table parameters
      const startY = 50;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 20;
      const tableWidth = doc.internal.pageSize.width - (2 * margin);
      const colWidths = [40, 25, 25, 35, 25, 25, 25, 30, 30]; // Property, ID, Postcode, Company, Date Live, AM, BM, Route, Status
      const rowHeight = 10;
      const headerHeight = 15;
      
      // Draw table headers
      doc.setFillColor(240, 240, 240);
      doc.rect(margin, startY, tableWidth, headerHeight, 'F');
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Property', margin + 5, startY + 10);
      doc.text('ID', margin + colWidths[0] + 5, startY + 10);
      doc.text('Postcode', margin + colWidths[0] + colWidths[1] + 5, startY + 10);
      doc.text('Company', margin + colWidths[0] + colWidths[1] + colWidths[2] + 5, startY + 10);
      doc.text('Date Live', margin + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3] + 5, startY + 10);
      doc.text('AM', margin + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3] + colWidths[4] + 5, startY + 10);
      doc.text('BM', margin + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3] + colWidths[4] + colWidths[5] + 5, startY + 10);
      doc.text('Route', margin + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3] + colWidths[4] + colWidths[5] + colWidths[6] + 5, startY + 10);
      doc.text('Status', margin + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3] + colWidths[4] + colWidths[5] + colWidths[6] + colWidths[7] + 5, startY + 10);
      
      // Draw table data
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      let currentY = startY + headerHeight;
      let pageNumber = 1;
      
      filteredProperties.forEach((property, index) => {
        // Check if we need a new page
        if (currentY + rowHeight > pageHeight - 30) {
          doc.addPage();
          pageNumber++;
          currentY = 20;
          
          // Add page number
          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');
          doc.text(`Page ${pageNumber}`, doc.internal.pageSize.width - 30, doc.internal.pageSize.height - 10);
        }
        
        // Draw row background (alternating colors)
        if (index % 2 === 0) {
          doc.setFillColor(248, 249, 250);
          doc.rect(margin, currentY, tableWidth, rowHeight, 'F');
        }
        
        // Draw row content
        let xPos = margin + 5;
        doc.text(String(property.propertyName || 'N/A').substring(0, 15), xPos, currentY + 7);
        
        xPos += colWidths[0] + 5;
        doc.text(String(property.propertyPublicId || 'N/A').substring(0, 8), xPos, currentY + 7);
        
        xPos += colWidths[1] + 5;
        doc.text(String(property.propertyPostcode || 'N/A').substring(0, 8), xPos, currentY + 7);
        
        xPos += colWidths[2] + 5;
        doc.text(String(property.clientName || 'N/A').substring(0, 12), xPos, currentY + 7);
        
        xPos += colWidths[3] + 5;
        doc.text(String(property.dateLive || 'N/A').substring(0, 8), xPos, currentY + 7);
        
        xPos += colWidths[4] + 5;
        doc.text(String(property.accountManager || 'N/A').substring(0, 8), xPos, currentY + 7);
        
        xPos += colWidths[5] + 5;
        doc.text(String(property.buildingManager || 'N/A').substring(0, 8), xPos, currentY + 7);
        
        xPos += colWidths[6] + 5;
        doc.text(String(property.routeName || 'N/A').substring(0, 12), xPos, currentY + 7);
        
        xPos += colWidths[7] + 5;
        doc.text(String(property.monthlyInspectionStatus || 'N/A').substring(0, 12), xPos, currentY + 7);
        
        currentY += rowHeight;
      });
      
      // Add page number to first page
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Page ${pageNumber}`, doc.internal.pageSize.width - 30, doc.internal.pageSize.height - 10);
      
      // Save the PDF
      doc.save('properties-monthly-inspections.pdf');
      
      toast.success('PDF exported successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const handleColumnVisibility = () => {
    setShowColumnVisibility(!showColumnVisibility);
  };

  // Handle table sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const toggleColumnVisibility = (columnKey) => {
    setColumnVisibility(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  // Click outside handler for column visibility dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(`.${styles.columnVisibilityDropdown}`) && 
          !event.target.closest(`.${styles.visibilityBtn}`)) {
        setShowColumnVisibility(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Only show loading spinner when actually loading data (after search has been performed)
  if (loading && hasSearched) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading properties monthly inspections...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Debug Toggle */}
      <div className={styles.debugToggle} style={{ display: 'none' }}>
        <label>
          <input 
            type="checkbox" 
            checked={false}
            disabled={true}
            readOnly={true}
          />
          Use Mock Data (Disabled - Live Data Only)
        </label>
        <span className={styles.apiStatus} data-status={apiStatus}>
          API Status: {apiStatus === 'connecting' && '🔄 Connecting...'}
          {apiStatus === 'loading' && '📡 Loading...'}
          {apiStatus === 'connected' && '✅ Connected'}
          {apiStatus === 'mock' && '📋 Mock Data'}
          {apiStatus === 'mock-fallback' && '🔄 Mock Fallback'}
          {apiStatus === 'error' && '❌ Error'}
        </span>
      </div>

      {/* Breadcrumbs */}
      <div className={styles.breadcrumbs}>
        <span 
          className={styles.breadcrumbLink}
          onClick={() => navigate('/dashboard')}
        >
          Dashboard
        </span>
        <span className={styles.breadcrumbSeparator}>/</span>
        <span 
          className={styles.breadcrumbLink}
          onClick={() => navigate('/properties')}
        >
          Properties
        </span>
        <span className={styles.breadcrumbSeparator}>/</span>
        <span className={styles.currentPage}>Monthly Inspections</span>
      </div>

      {/* Properties Overview */}
      <div className={styles.propertiesOverview}>
        <div className={styles.panelTitle}>
          <h3>Properties overview</h3>
        </div>
        <div className={styles.panelGroup}>
          <div 
            className={styles.activityBox} 
            style={{ backgroundColor: '#5bc0de' }}
            onClick={() => handleStatusBoxClick('upcoming')}
          >
            <p className={styles.activityValue}>{statusOverview.propertiesUpcoming}</p>
            <p className={styles.activityTitle}>To be Scheduled</p>
          </div>
          
          <div 
            className={styles.activityBox} 
            style={{ backgroundColor: '#f0ad4e' }}
            onClick={() => handleStatusBoxClick('scheduled')}
          >
            <p className={styles.activityValue}>{statusOverview.propertiesInspectionScheduled}</p>
            <p className={styles.activityTitle}>Inspection Scheduled</p>
          </div>
          
          <div 
            className={styles.activityBox} 
            style={{ backgroundColor: '#5cb85c' }}
            onClick={() => handleStatusBoxClick('completed')}
          >
            <p className={styles.activityValue}>{statusOverview.propertiesInspectionCompleted}</p>
            <p className={styles.activityTitle}>Completed</p>
          </div>
          
          <div 
            className={styles.activityBox} 
            style={{ backgroundColor: '#337ab7' }}
            onClick={() => handleStatusBoxClick('to_be_published')}
          >
            <p className={styles.activityValue}>{statusOverview.propertiesToBePublished}</p>
            <p className={styles.activityTitle}>To be published</p>
          </div>
          
          <div 
            className={styles.activityBox} 
            style={{ backgroundColor: '#5cb85c' }}
            onClick={() => handleStatusBoxClick('past_completed')}
          >
            <p className={styles.activityValue}>{statusOverview.propertiesCompleted}</p>
            <p className={styles.activityTitle}>Past Inspection To Complete</p>
          </div>
          
          <div 
            className={styles.activityBox} 
            style={{ backgroundColor: '#d9534f' }}
            onClick={() => handleStatusBoxClick('overdue')}
          >
            <p className={styles.activityValue}>{statusOverview.propertiesOverdue}</p>
            <p className={styles.activityTitle}>Overdue</p>
          </div>
        </div>
      </div>

      {/* Status Descriptions Panel */}
      <div className={styles.statusDescriptionsPanel}>
        <div 
          className={styles.panelTitle} 
          onClick={toggleStatusDescriptions}
          style={{ cursor: 'pointer' }}
        >
          <h3>Descriptions of statuses</h3>
          <i 
            className={`fa fa-arrow-${isStatusDescriptionsCollapsed ? 'down' : 'up'}`} 
            aria-hidden="true"
            style={{ display: 'inline-block' }}
          ></i>
        </div>
        <div 
          className={styles.panelBody}
          style={{ 
            display: isStatusDescriptionsCollapsed ? 'none' : 'block',
            transition: 'all 0.3s ease'
          }}
        >
          <div className={styles.statusDescription}>
            <span className={styles.statusLabel} style={{ backgroundColor: '#5bc0de' }}>
              <strong>To be Scheduled</strong>
            </span> - there is no inspection for this month
          </div>
          <div className={styles.statusDescription}>
            <span className={styles.statusLabel} style={{ backgroundColor: '#f0ad4e' }}>
              <strong>Inspection Scheduled</strong>
            </span> - monthly inspection was created and is upcoming
          </div>
          <div className={styles.statusDescription}>
            <span className={styles.statusLabel} style={{ backgroundColor: '#5cb85c' }}>
              <strong>Completed</strong>
            </span> - monthly inspection was done
          </div>
          <div className={styles.statusDescription}>
            <span className={styles.statusLabel} style={{ backgroundColor: '#337ab7' }}>
              <strong>To be published</strong>
            </span> - report is not published
          </div>
          <div className={styles.statusDescription}>
            <span className={styles.statusLabel} style={{ backgroundColor: '#5cb85c' }}>
              <strong>Past Inspection To Complete</strong>
            </span> - all Monthly Inspection Reports are added
          </div>
          <div className={styles.statusDescription}>
            <span className={styles.statusLabel} style={{ backgroundColor: '#d9534f' }}>
              <strong>Overdue</strong>
            </span> - lack of at least one published Monthly Inspection Report
          </div>
        </div>
      </div>

      {/* Monthly Inspections Panel */}
      <div className={styles.monthlyInspectionsPanel}>
        <div className={styles.panelTitle}>
          <h3>Monthly Inspections</h3>
        </div>
        <div className={styles.panelBody}>
          {/* Search Form */}
          <form className={styles.searchForm} onSubmit={handleSearchSubmit}>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.controlLabel}>Account Manager</label>
                <div className={styles.formControlWrapper}>
                  <select 
                    className={styles.formControl}
                    value={searchForm.accountManagerId}
                    onChange={(e) => handleSearchChange('accountManagerId', e.target.value)}
                  >
                    <option value="all">All</option>
                    {accountManagers.map(manager => (
                      <option key={manager.id} value={manager.id}>{manager.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.controlLabel}>Route</label>
                <div className={styles.formControlWrapper}>
                  <select 
                    className={styles.formControl}
                    value={searchForm.inspectionRouteId}
                    onChange={(e) => handleSearchChange('inspectionRouteId', e.target.value)}
                  >
                    <option value="all">All</option>
                    {routes.map(route => (
                      <option key={route.id} value={route.id}>{route.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.controlLabel}>Client</label>
                <div className={styles.formControlWrapper} style={{ position: 'relative' }}>
                  <input 
                    type="text"
                    className={styles.formControl}
                    placeholder="Type client name"
                    value={searchForm.clientName}
                    onChange={(e) => handleClientSearch(e.target.value)}
                    onBlur={() => setTimeout(() => setShowClientTypeahead(false), 200)}
                    onFocus={() => searchForm.clientName && handleClientSearch(searchForm.clientName)}
                  />
                  {showClientTypeahead && clientTypeaheadResults.length > 0 && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      zIndex: 1000,
                      backgroundColor: 'white',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                      {clientTypeaheadResults.map((client) => (
                        <div
                          key={client.id}
                          onMouseDown={(e) => {
                            e.preventDefault(); // Prevent input blur
                            handleClientSelect(client);
                          }}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #eee'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                        >
                          {client.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.controlLabel}>Property</label>
                <div className={styles.formControlWrapper} style={{ position: 'relative' }}>
                  <input 
                    type="text"
                    className={styles.formControl}
                    placeholder="Type property name"
                    value={searchForm.propertyName}
                    onChange={(e) => handlePropertySearch(e.target.value)}
                    onBlur={() => setTimeout(() => setShowPropertyTypeahead(false), 200)}
                    onFocus={() => searchForm.propertyName && handlePropertySearch(searchForm.propertyName)}
                  />
                  {showPropertyTypeahead && propertyTypeaheadResults.length > 0 && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      zIndex: 1000,
                      backgroundColor: 'white',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                      {propertyTypeaheadResults.map((property) => (
                        <div
                          key={property.id}
                          onMouseDown={(e) => {
                            e.preventDefault(); // Prevent input blur
                            handlePropertySelect(property);
                          }}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #eee'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                        >
                          {property.name} ({property.property_id || property.id})
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className={styles.searchButtonContainer}>
                <button 
                  type="submit" 
                  className={styles.btnPrimary}
                >
                  Search
                </button>
              </div>
            </div>
          </form>

          {!hasSearched ? (
            <div className={styles.noResults}>Please use the filters above and click "Search" to load properties.</div>
          ) : (
            <div>
              {/* DataTables Control Bar */}
              <div className={styles.dataTableControls}>
                <div className={styles.entriesControl}>
                  <label>Show</label>
                  <select 
                    className={styles.entriesSelect}
                    value={entriesPerPage}
                    onChange={(e) => {
                      const newValue = Number(e.target.value);
                      setEntriesPerPage(newValue);
                      setCurrentPage(1);
                    }}
                  >
                    <option value={30}>30</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={75}>75</option>
                    <option value={100}>100</option>
                  </select>
                  <span>entries</span>
                </div>

                <div className={styles.searchControl}>
                  <label>Search:</label>
                  <div className={styles.searchInputGroup}>
                    <input
                      type="text"
                      className={styles.searchInput}
                      placeholder="Search properties..."
                      value={tableSearch}
                                             onChange={(e) => {
                         setTableSearch(e.target.value);
                         setCurrentPage(1);
                       }}
                    />
                  </div>
                </div>

                <div className={styles.actionButtons}>
                  <button 
                    className={styles.exportBtn}
                    onClick={handleExportCSV}
                  >
                    Export as CSV
                  </button>
                  <button 
                    className={styles.exportBtn}
                    onClick={handleExportPDF}
                  >
                    Export as PDF
                  </button>
                  <div className={styles.visibilityBtnContainer}>
                    <button 
                      className={styles.visibilityBtn}
                      onClick={handleColumnVisibility}
                    >
                      Column visibility
                    </button>
                    {showColumnVisibility && (
                      <div className={styles.columnVisibilityDropdown}>
                        <h4>Toggle Columns:</h4>
                        <label className={styles.columnToggle}>
                          <input
                            type="checkbox"
                            checked={columnVisibility.property}
                            onChange={() => toggleColumnVisibility('property')}
                          />
                          Property
                        </label>
                        <label className={styles.columnToggle}>
                          <input
                            type="checkbox"
                            checked={columnVisibility.id}
                            onChange={() => toggleColumnVisibility('id')}
                          />
                          ID
                        </label>
                        <label className={styles.columnToggle}>
                          <input
                            type="checkbox"
                            checked={columnVisibility.postcode}
                            onChange={() => toggleColumnVisibility('postcode')}
                          />
                          Postcode
                        </label>
                        <label className={styles.columnToggle}>
                          <input
                            type="checkbox"
                            checked={columnVisibility.company}
                            onChange={() => toggleColumnVisibility('company')}
                          />
                          Company
                        </label>
                        <label className={styles.columnToggle}>
                          <input
                            type="checkbox"
                            checked={columnVisibility.dateLive}
                            onChange={() => toggleColumnVisibility('dateLive')}
                          />
                          Date Live
                        </label>
                        <label className={styles.columnToggle}>
                          <input
                            type="checkbox"
                            checked={columnVisibility.am}
                            onChange={() => toggleColumnVisibility('am')}
                          />
                          AM
                        </label>
                        <label className={styles.columnToggle}>
                          <input
                            type="checkbox"
                            checked={columnVisibility.bm}
                            onChange={() => toggleColumnVisibility('bm')}
                          />
                          BM
                        </label>
                        <label className={styles.columnToggle}>
                          <input
                            type="checkbox"
                            checked={columnVisibility.route}
                            onChange={() => toggleColumnVisibility('route')}
                          />
                          Route
                        </label>
                        <label className={styles.columnToggle}>
                          <input
                            type="checkbox"
                            checked={columnVisibility.status}
                            onChange={() => toggleColumnVisibility('status')}
                          />
                          Status
                        </label>
                        <label className={styles.columnToggle}>
                          <input
                            type="checkbox"
                            checked={columnVisibility.actions}
                            onChange={() => toggleColumnVisibility('actions')}
                          />
                          Actions
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className={styles.resultCount}>
                (Showing {filteredProperties.length} of {totalRecords} results)
              </div>

              {/* Data Table */}
              <div className={styles.tableWrapper}>
                <table className={styles.dataTable}>
                  <thead>
                    <tr>
                      {columnVisibility.property && (
                        <th 
                          className="sortable"
                          onClick={() => handleSort("property")}
                          style={{ cursor: "pointer" }}
                        >
                          Property<SortIcon direction={sortField === "property" ? sortDirection : null} isActive={sortField === "property"} />
                        </th>
                      )}
                      {columnVisibility.id && (
                        <th 
                          className="sortable"
                          onClick={() => handleSort("id")}
                          style={{ cursor: "pointer" }}
                        >
                          ID<SortIcon direction={sortField === "id" ? sortDirection : null} isActive={sortField === "id"} />
                        </th>
                      )}
                      {columnVisibility.postcode && (
                        <th 
                          className="sortable"
                          onClick={() => handleSort("postcode")}
                          style={{ cursor: "pointer" }}
                        >
                          Postcode<SortIcon direction={sortField === "postcode" ? sortDirection : null} isActive={sortField === "postcode"} />
                        </th>
                      )}
                      {columnVisibility.company && (
                        <th 
                          className="sortable"
                          onClick={() => handleSort("company")}
                          style={{ cursor: "pointer" }}
                        >
                          Company<SortIcon direction={sortField === "company" ? sortDirection : null} isActive={sortField === "company"} />
                        </th>
                      )}
                      {columnVisibility.dateLive && (
                        <th 
                          className="sortable"
                          onClick={() => handleSort("dateLive")}
                          style={{ cursor: "pointer" }}
                        >
                          Date Live<SortIcon direction={sortField === "dateLive" ? sortDirection : null} isActive={sortField === "dateLive"} />
                        </th>
                      )}
                      {columnVisibility.am && (
                        <th 
                          className="sortable"
                          onClick={() => handleSort("am")}
                          style={{ cursor: "pointer" }}
                        >
                          AM<SortIcon direction={sortField === "am" ? sortDirection : null} isActive={sortField === "am"} />
                        </th>
                      )}
                      {columnVisibility.bm && (
                        <th 
                          className="sortable"
                          onClick={() => handleSort("bm")}
                          style={{ cursor: "pointer" }}
                        >
                          BM<SortIcon direction={sortField === "bm" ? sortDirection : null} isActive={sortField === "bm"} />
                        </th>
                      )}
                      {columnVisibility.route && (
                        <th 
                          className="sortable"
                          onClick={() => handleSort("route")}
                          style={{ cursor: "pointer" }}
                        >
                          Route<SortIcon direction={sortField === "route" ? sortDirection : null} isActive={sortField === "route"} />
                        </th>
                      )}
                      {columnVisibility.status && (
                        <th 
                          className="sortable"
                          onClick={() => handleSort("status")}
                          style={{ cursor: "pointer" }}
                        >
                          Status<SortIcon direction={sortField === "status" ? sortDirection : null} isActive={sortField === "status"} />
                        </th>
                      )}
                      {columnVisibility.actions && <th className={styles.actionHead}>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProperties.map((property) => (
                      <tr key={property.id}>
                        {columnVisibility.property && (
                          <td>
                            <span
                              onClick={() => navigate(`/properties/${property.id}`)}
                              style={{ color: '#007bff', cursor: 'pointer', textDecoration: 'underline' }}
                            >
                              {property.propertyName}
                            </span>
                          </td>
                        )}
                        {columnVisibility.id && <td>{property.propertyPublicId}</td>}
                        {columnVisibility.postcode && <td>{property.propertyPostcode}</td>}
                        {columnVisibility.company && <td>{property.clientName}</td>}
                        {columnVisibility.dateLive && <td>{property.dateLive}</td>}
                        {columnVisibility.am && <td>{property.accountManager}</td>}
                        {columnVisibility.bm && <td>{property.buildingManager}</td>}
                        {columnVisibility.route && <td>{property.routeName}</td>}
                        {columnVisibility.status && (
                          <td>
                            <span 
                              className={styles.statusBadge} 
                              style={{ backgroundColor: property.statusColor }}
                            >
                              {property.monthlyInspectionStatus}
                            </span>
                          </td>
                        )}
                        {columnVisibility.actions && (
                          <td className={styles.actionColumn}>
                            <div className={styles.actionBtnGroup}>
                              <button
                                className="btn btn-warning btn-xs"
                                onClick={() => handlePropertyAction('show', property.id)}
                                title="Show property details"
                              >
                                Show
                              </button>
                              {!isGuardianManager() && (
                                <button
                                  className="btn btn-primary btn-xs"
                                  onClick={() => navigate(`/properties/${property.id}/monthly-inspections`)}
                                  title="Edit monthly inspection form"
                                >
                                  Edit
                                </button>
                              )}
                              {!isGuardianManager() && (
                                <button
                                  className="btn btn-danger btn-xs"
                                  onClick={() => handlePropertyAction('remove', property.id)}
                                  title="Remove property"
                                >
                                  Del
                                </button>
                              )}
                              {!isGuardianManager() && (
                                <button
                                  className="btn btn-info btn-xs"
                                  onClick={() => handlePropertyAction('payments', property.id)}
                                  title="Property payments"
                                >
                                  Pay
                                </button>
                              )}
                              {!isGuardianManager() && (
                                <button
                                  className="btn btn-success btn-xs"
                                  onClick={() => handlePropertyAction('photos', property.id)}
                                  title="Property photos"
                                >
                                  Pics
                                </button>
                              )}
                              <button
                                className="btn btn-info btn-xs"
                                onClick={() => handlePropertyAction('viewings', property.id)}
                                title="Property viewings"
                              >
                                View
                              </button>
                              <button
                                className="btn btn-success btn-xs"
                                onClick={() => handlePropertyAction('monthly-inspections', property.id)}
                                title="Monthly inspections"
                              >
                                Inspect
                              </button>
                              <button
                                className="btn btn-warning btn-xs"
                                onClick={() => handlePropertyAction('copy-address', property.id)}
                                title="Copy address to clipboard"
                              >
                                Copy
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className={styles.pagination}>
                  <div className={styles.paginationInfo}>
                    Showing {((currentPage - 1) * entriesPerPage) + 1} to {Math.min(currentPage * entriesPerPage, totalRecords)} of {totalRecords} entries
                  </div>
                  <div className={styles.paginationControls}>
                    <button
                      className={styles.paginationBtn}
                      onClick={goToPreviousPage}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>

                    {getPageNumbers().map(page => (
                      <button
                        key={page}
                        className={`${styles.paginationBtn} ${page === currentPage ? styles.activePage : ''}`}
                        onClick={() => goToPage(page)}
                      >
                        {page}
                      </button>
                    ))}

                    <button
                      className={styles.paginationBtn}
                      onClick={goToNextPage}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}

              {/* No results message */}
              {filteredProperties.length === 0 && (
                <div className={styles.noResults}>No properties found matching your criteria.</div>
              )}
            </div>
          )}
        </div>
      </div>

      {useMockData && (
        <div className={styles.demoMode}>
          <strong>Demo Mode:</strong> This is running with mock data. Toggle the checkbox in the top-right corner to test with real API.
        </div>
      )}
    </div>
  );
};

export default PropertiesMonthlyInspections;  